import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Switch, Tooltip, Typography } from 'antd';
import { ExportPriceBookButton } from 'components/atoms/ExportPriceBookButton';
import { ImportPriceBookModal } from 'components/molecules/ImportPriceBookModal';
import { Stack } from 'components/UI';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setPriceBookProductNumber, setPriceBookCondition, setPriceBookEffectiveDate, setPriceBookEffectiveDateRange, resetPriceBookParams } from 'redux/services/julia/priceBookEntryParams';
import { useAppSelector } from 'redux/store';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface PriceBookControlsProps {
  isReadOnly?: boolean;
}

export const PriceBookControls = ({ isReadOnly }: PriceBookControlsProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const { effectiveDate, sourceProductNumber } = useAppSelector((state) => state.juliaPriceBookEntryParams);
  const isProductContext = !!sourceProductNumber;

  const isDivisionContext = location.pathname.includes('division/');
  const backPath = isDivisionContext ? `/division/${id}` : `/business/${id}`;

  const isShowingAll = !effectiveDate;

  const handleProductNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setPriceBookProductNumber(e.target.value || undefined));
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setPriceBookCondition(e.target.value || undefined));
  };

  const handleDateRangeChange = (dates: [moment.Moment | null, moment.Moment | null] | null): void => {
    if (dates && dates[0] && dates[1]) {
      dispatch(
        setPriceBookEffectiveDateRange({
          from: dates[0].startOf('day').toISOString(),
          to: dates[1].endOf('day').toISOString()
        })
      );
    } else {
      dispatch(setPriceBookEffectiveDateRange({ from: undefined, to: undefined }));
    }
  };

  const handleEffectiveNowChange = (checked: boolean): void => {
    if (checked) {
      dispatch(setPriceBookEffectiveDate(new Date().toISOString()));
    } else {
      dispatch(setPriceBookEffectiveDate(undefined));
    }
  };

  const handleBackToCatalog = (): void => {
    dispatch(resetPriceBookParams());
    navigate(backPath);
  };

  return (
    <Stack flexDirection="column" gap={16}>
      <Stack justifyContent="space-between" alignItems="flex-end">
        <Stack flexDirection="column">
          <AntPageTitle text={isProductContext ? `Prices — ${sourceProductNumber}` : isReadOnly ? 'Price Book Entries (Inherited)' : 'Price Book Entries'} />
          <Stack gap={10} alignItems="center">
            {!isProductContext && <Input placeholder="Product Number" allowClear onChange={handleProductNumberChange} style={{ width: 200 }} />}
            <Input placeholder="Condition" allowClear onChange={handleConditionChange} style={{ width: 160 }} />
            <RangePicker placeholder={['Effective From', 'Effective To']} onChange={handleDateRangeChange} style={{ width: 280 }} allowClear />
            <Stack gap={6} alignItems="center">
              <Switch size="small" checked={!isShowingAll} onChange={handleEffectiveNowChange} />
              <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                Effective Now
              </Text>
              <Tooltip title="When on, only entries currently in effect are shown — active entries where today falls between the effective date and end date. Turn off to include inactive and expired entries.">
                <InfoCircleOutlined style={{ color: '#999', fontSize: 13, cursor: 'help' }} />
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
        <Stack gap={8}>
          <Button onClick={handleBackToCatalog} icon={<ArrowLeftOutlined />}>
            {isProductContext ? 'Product Catalog' : 'Back to Catalog Items'}
          </Button>
          {!isProductContext && !isReadOnly && <ImportPriceBookModal />}
          {!isProductContext && <ExportPriceBookButton />}
        </Stack>
      </Stack>
    </Stack>
  );
};
